import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash, FaArchive, FaEye, FaPlus, FaUndo } from 'react-icons/fa';
import { apiService } from '../../../components/Config/api.service';

const CustomReportManager = ({ onClose, onEdit, onCreate, menuItems }) => {
  const { t } = useTranslation();
  const [customReports, setCustomReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'archived'

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const resp = await apiService.getElement('/custom-report-templates?include_archived=1');
      if (resp?.data?.status === 'success' && Array.isArray(resp.data.data)) {
        const reports = resp.data.data.map(t => ({
          id: `custom_${t.id}`,
          rawId: t.id,
          label: t.name,
          report_name: `custom_report_${t.id}`,
          doc_name: t.doc_name || t.name,
          description: t.note || '',
          aggregation_type: t.aggregation_type || 'per_payslip',
          department_id: t.department_id || null,
          assignment_id: t.assignment_id || null,
          active: t.active !== undefined ? t.active : true,
          columns: t.columns || []
        }));
        setCustomReports(reports);
      } else {
        const customs = menuItems.filter(m => String(m.id).startsWith('custom_')).map(m => ({
          ...m,
          rawId: String(m.id).replace('custom_', ''),
          active: true
        }));
        setCustomReports(customs);
      }
    } catch (err) {
      console.error('Erreur chargement templates:', err);
      const customs = menuItems.filter(m => String(m.id).startsWith('custom_')).map(m => ({
        ...m,
        rawId: String(m.id).replace('custom_', ''),
        active: true
      }));
      setCustomReports(customs);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleToggleArchive = async (report) => {
    const isArchiving = report.active;
    const confirmMsg = isArchiving
      ? t('customReportManager.archiveConfirm', { name: report.label })
      : t('customReportManager.unarchiveConfirm', { name: report.label });

    const confirmed = window.confirm(confirmMsg);
    if (!confirmed) return;

    try {
      await apiService.saveElement('/custom-report-template/archive', {
        template_id: report.rawId,
        active: !isArchiving
      });
      setCustomReports(prev => prev.map(r => r.id === report.id ? { ...r, active: !isArchiving } : r));
    } catch (err) {
      console.error('Erreur archivage:', err);
      alert(isArchiving ? t('customReportManager.archiveError') : t('customReportManager.unarchiveError'));
    }
  };

  const handleDelete = async (report) => {
    const confirmed = window.confirm(
      t('customReportManager.deleteConfirm', { name: report.label })
    );
    if (!confirmed) return;

    const deleteWord = t('customReportManager.deleteConfirmWord');
    const doubleCheck = window.prompt(t('customReportManager.deleteDoubleCheckPrompt', { word: deleteWord }));
    if (doubleCheck !== deleteWord) return;

    try {
      await apiService.saveElement('/custom-report-template/delete', { template_id: report.rawId });
      setCustomReports(prev => prev.filter(r => r.id !== report.id));
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert(t('customReportManager.deleteError'));
    }
  };

  const filteredReports = customReports.filter(r => {
    const matchesTab = activeTab === 'active' ? r.active : !r.active;
    const matchesSearch =
      r.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('customReportManager.title')}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('customReportManager.reportCount', { count: customReports.length })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            aria-label={t('customReportManager.close')}
          >
            ×
          </button>
        </div>

        {/* Search & Tabs */}
        <div className="px-6 py-4 border-b border-gray-200 space-y-3">
          <div className="flex gap-2 border-b">
            <button
              className={`pb-2 px-4 font-semibold text-sm transition-colors border-b-2 ${
                activeTab === 'active'
                  ? 'border-[--color-primary] text-[--color-primary]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('active')}
            >
              {t('customReportManager.tabActive')} ({customReports.filter(r => r.active).length})
            </button>
            <button
              className={`pb-2 px-4 font-semibold text-sm transition-colors border-b-2 ${
                activeTab === 'archived'
                  ? 'border-[--color-primary] text-[--color-primary]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('archived')}
            >
              {t('customReportManager.tabArchived')} ({customReports.filter(r => !r.active).length})
            </button>
          </div>

          <input
            type="text"
            placeholder={t('customReportManager.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
          />
          <button
            onClick={onCreate}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] transition-colors"
          >
            <FaPlus size={16} />
            {t('customReportManager.createNew')}
          </button>
        </div>

        {/* Reports List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="text-center py-12 text-gray-500">{t('customReportManager.loading')}</div>
          ) : filteredReports.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <FaEye size={48} className="mx-auto" />
              </div>
              <p className="text-gray-600">
                {searchQuery ? t('customReportManager.noReportFound') : t('customReportManager.noCustomReportCreated')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReports.map((report) => (
                <div
                  key={report.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-[--color-primary] transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {report.label}
                        </h3>
                        {!report.active && (
                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 rounded font-medium">
                            {t('customReportManager.archived')}
                          </span>
                        )}
                      </div>
                      {report.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {report.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                          Excel
                        </span>
                        <span className="text-xs text-gray-500">
                          {report.report_name}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => onEdit(report)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title={t('customReportManager.edit')}
                        aria-label={t('customReportManager.editLabel', { name: report.label })}
                      >
                        <FaEdit size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleArchive(report)}
                        className={`p-2 rounded-lg transition-colors ${
                          report.active
                            ? 'text-amber-600 hover:bg-amber-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={report.active ? t('customReportManager.archive') : t('customReportManager.unarchive')}
                        aria-label={
                          report.active
                            ? t('customReportManager.archiveLabel', { name: report.label })
                            : t('customReportManager.unarchiveLabel', { name: report.label })
                        }
                      >
                        {report.active ? <FaArchive size={18} /> : <FaUndo size={18} />}
                      </button>
                      <button
                        onClick={() => handleDelete(report)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('hspForm.delete')}
                        aria-label={t('customReportManager.deleteLabel', { name: report.label })}
                      >
                        <FaTrash size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('customReportManager.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomReportManager;